import { addHours, differenceInMinutes, format, parse } from 'date-fns';

export interface ClockRange {
  /** "HH:mm" wall-clock. */
  start: string;
  end: string;
}

// Slot times are zone-less wall-clock, so they're parsed onto one fixed day and
// only ever compared with each other.
const REFERENCE_DAY = new Date(2000, 0, 1);

/** An "HH:mm" or "HH:mm:ss" wall-clock string as a Date on the reference day. */
export const parseClock = (clock: string) => parse(clock.slice(0, 5), 'HH:mm', REFERENCE_DAY);

const formatClock = (date: Date) => format(date, 'HH:mm');

/**
 * Splits a wall-clock window into one-hour chunks: 10:00–12:00 → 10–11, 11–12. A
 * trailing remainder shorter than an hour is folded into the last chunk so nothing
 * of the window is lost (10:00–12:30 → 10–11, 11–12:30).
 */
export const splitIntoHourChunks = (start: string, end: string): ClockRange[] => {
  const windowStart = parseClock(start);
  const windowEnd = parseClock(end);
  if (differenceInMinutes(windowEnd, windowStart) <= 60) {
    return [{ start: formatClock(windowStart), end: formatClock(windowEnd) }];
  }

  const chunks: ClockRange[] = [];
  let cursor = windowStart;
  while (differenceInMinutes(windowEnd, cursor) >= 60) {
    const next = addHours(cursor, 1);
    const isLast = differenceInMinutes(windowEnd, next) < 60;
    chunks.push({ start: formatClock(cursor), end: formatClock(isLast ? windowEnd : next) });
    cursor = next;
  }
  return chunks;
};
